// composables/tabulator/useTabulatorLocalTable.ts
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import type { Ref } from 'vue'
import { TabulatorFull as Tabulator } from 'tabulator-tables'
import type { ColumnDefinition } from 'tabulator-tables'
import '@/assets/scss/tabulator.scss'

declare module '#app' {
  interface NuxtApp {
    $registerTabulatorTable: (table: Tabulator) => void
  }
}

export function useTabulatorLocalTable(options: {
  columns: ColumnDefinition[]
  data: Ref<any[]>
  persistenceID: string
  layout?: 'fitData' | 'fitColumns'
  placeholder?: string
  buildSearchText?: (rowData: any) => string
}) {
  const table = ref<HTMLElement | null>(null)
  const tabulator = ref<Tabulator | null>(null)
  const searchTerm = ref('')
  const { getPaginationSize, setupPaginationListener } = useTabulatorSettings()
  const nuxtApp = useNuxtApp()
  const registerTable = nuxtApp.$registerTabulatorTable
  const { $i18n } = useNuxtApp()

  watch(searchTerm, (value) => {
    if (!tabulator.value) return

    const terms = value.toLowerCase().split(/\s+/).filter(Boolean)
    tabulator.value.setFilter((rowData) => {
      const combined = (options.buildSearchText ? options.buildSearchText(rowData) : flattenForSearch(rowData)).toLowerCase()
      return terms.every(term => combined.includes(term))
    })
  })

  watch(options.data, async () => {
    await nextTick()
    if (tabulator.value) {
      tabulator.value.setData(options.data.value)
    }
  })

  onMounted(async () => {
    await nextTick() // ⏳ wait for DOM to be rendered

    if (!table.value) return

    tabulator.value = new Tabulator(table.value, {
      data: options.data.value,
      reactiveData: true,
      columns: options.columns.map(c => ({ title: (c.headerLocaleId ? $i18n.t(c.headerLocaleId) : c.title), vertAlign: 'middle', ...c })),
      layout: options.layout ?? 'fitData',
      rowHeight: TABULATOR.ROW_HEIGHT,
      ...(window.innerWidth > 1024 ? { height: TABULATOR.HEIGHT } : {}),
      placeholder: options.placeholder ?? $i18n.t('common.table.no_results_found'),
      pagination: true,
      paginationCounter: "rows",
      paginationSize: getPaginationSize(),
      paginationSizeSelector: [5, 10, 20, 50],
      persistenceID: options.persistenceID,
    })

    setupPaginationListener(tabulator.value)
    registerTable?.(tabulator.value)
  })

  onBeforeUnmount(() => {
    tabulator.value?.destroy()
  })

  return { table, tabulator, searchTerm }
}
